import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';

import Navbar from '../components/common/navbar';
import OfficeImg1 from '../assets/images/OfficeCanteen.jpg';
import OfficeImg2 from '../assets/images/OfficeCollaboration.jpg';
import OfficeImg3 from '../assets/images/OfficeTeam.jpg';
import {
  Fullpage,
  FullPageSections,
  FullpageSection,
} from '@ap.cx/react-fullpage';

const openRoles = [
  {
    index: '01',
    title: 'UI/UX, Visual Designer',
    description: `We are looking for an experienced full-time digital designer to join our team and help shape the future of companies around the world. You will work closely with our Creative Director to design products, websites and accompanying graphics.`,
    tags: [
      'Figma',
      'Wireframes & User Flows',
      'Prototypes',
      'UX Research',
      'Design System',
    ],
  },
  {
    index: '02',
    title: 'Front-end Developer',
    description: `We are looking for a front-end developer who is passionate about building fast, pixel-perfect interfaces. You will collaborate closely with designers and back-end engineers to deliver exceptional digital products.`,
    tags: [
      'React',
      'Tailwind CSS',
      'TypeScript',
      'Next.js',
      'Performance Optimization',
    ],
  },
  {
    index: '03',
    title: 'Project Manager',
    description: `We need a driven project manager to own delivery across multiple client projects. You will coordinate between teams, manage timelines, and ensure every project ships on time and to the highest standard.`,
    tags: [
      'Agile',
      'Trello / Jira',
      'Client Communication',
      'Risk Management',
      'Scrum',
    ],
  },
  {
    index: '04',
    title: 'Digital Marketing Manager',
    description: `We are hiring a performance-focused digital marketer to lead campaigns across SEO, paid media, and social. You will work directly with clients to drive measurable growth across Fintech, Healthcare & Ecommerce.`,
    tags: ['Meta Ads', 'Google Ads', 'SEO', 'Analytics', 'Content Strategy'],
  },
];

function ApplicationModal({ role, onClose }) {
  const formRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    applyingFor: role?.title || '',
    resumeLink: '',
    message: '',
  });
  const [status, setStatus] = useState('idle');

  function handleChange(field) {
    return e => setForm(prev => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('sending');
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          applyingFor: form.applyingFor,
          resumeLink: form.resumeLink,
          message: form.message,
          to_email: 'sarthak@buildatscale.com',
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-lg bg-black border border-white/30 rounded-2xl p-6 lg:p-8 relative max-h-[90vh] overflow-y-auto no-scrollbar"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/30 hover:text-white text-[22px] leading-none transition-colors duration-150"
        >
          x
        </button>

        <h3 className="text-white font-lato font-[700] text-[18px] lg:text-[22px] mb-1">
          Apply for{' '}
          <span className="text-[#24FFFF]">{form.applyingFor || 'a role'}</span>
        </h3>
        <p className="text-white/40 font-lato text-[12px] lg:text-[13px] mb-6">
          Fill in your details and we will get back to you shortly.
        </p>

        {status === 'success' ? (
          <div className="flex flex-col items-center gap-y-3 py-10 text-center">
            <span className="text-[#24FFFF] text-[40px]">checkmark</span>
            <p className="text-white font-lato font-[600] text-[16px]">
              Application sent!
            </p>
            <p className="text-white/40 font-lato text-[13px]">
              We will review your application and reach out soon.
            </p>
            <button
              onClick={onClose}
              className="mt-4 bg-primary text-white font-lato font-[500] text-[0.85em] px-8 py-2.5 rounded-[30px]"
            >
              Close
            </button>
          </div>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="flex flex-col gap-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-y-2">
                <Label htmlFor="name">Full name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Jane Smith"
                  value={form.name}
                  onChange={handleChange('name')}
                  required
                />
              </div>
              <div className="flex flex-col gap-y-2">
                <Label htmlFor="email">Email address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="jane@example.com"
                  value={form.email}
                  onChange={handleChange('email')}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-y-2">
                <Label htmlFor="phone">Phone number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={form.phone}
                  onChange={handleChange('phone')}
                />
              </div>
              <div className="flex flex-col gap-y-2">
                <Label htmlFor="applyingFor">Applying for *</Label>
                <select
                  id="applyingFor"
                  value={form.applyingFor}
                  onChange={handleChange('applyingFor')}
                  required
                  className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2 text-[13px] text-white font-lato font-[400] outline-none focus:border-neutral-700 transition-colors duration-200 h-10"
                >
                  <option value="">Select a role</option>
                  {openRoles.map(r => (
                    <option key={r.index} value={r.title}>
                      {r.title}
                    </option>
                  ))}
                  <option value="General Application">
                    General Application
                  </option>
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-y-2">
              <Label htmlFor="resumeLink">Resume / Portfolio link</Label>
              <Input
                id="resumeLink"
                type="url"
                placeholder="https://drive.google.com/..."
                value={form.resumeLink}
                onChange={handleChange('resumeLink')}
              />
            </div>

            <div className="flex flex-col gap-y-2">
              <Label htmlFor="message">Message</Label>
              <Input
                id="message"
                type="text"
                placeholder="Tell us a bit about yourself and why you would be a great fit..."
                value={form.message}
                onChange={handleChange('message')}
              />
            </div>

            {status === 'error' && (
              <p className="text-red-400 font-lato text-[12px]">
                Something went wrong. Please try emailing us directly at
                sarthak@buildatscale.com
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="group/btn relative block h-10 w-full rounded-md bg-gradient-to-br from-black to-neutral-600 font-medium text-white shadow-[0px_1px_0px_0px_#ffffff40_inset,0px_-1px_0px_0px_#ffffff40_inset] dark:bg-zinc-800 dark:from-zinc-900 dark:to-zinc-900 dark:shadow-[0px_1px_0px_0px_#27272a_inset,0px_-1px_0px_0px_#27272a_inset]"
            >
              {status === 'sending' ? 'Sending...' : 'Submit application'}
              <BottomGradient />
            </button>
          </form>
        )}
      </motion.div>
    </div>,
    document.body
  );
}

function Culture() {
  return (
    <div className="bg-black h-screen px-5 pt-24 pb-10 lg:px-20 lg:py-0 flex flex-col justify-center">
      <div className="mb-6 lg:mb-10">
        <h2 className="text-white font-lato font-[800] text-[2.2em] lg:text-[4.5em] leading-tight max-w-3xl">
          Careers @ <span className="text-[#24FFFF]">buildatscale</span>
        </h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-20 w-full items-center">
        <div className="flex flex-col gap-y-4 lg:gap-y-5">
          <p className="text-white/40 font-lato text-[12px] lg:text-[13px] font-[600] tracking-[0.2em] uppercase">
            Our culture
          </p>
          <h2 className="text-white font-lato font-[800] text-[1.6em] lg:text-[3.2em] leading-tight">
            Here, anything is <span className="text-[#24FFFF]">possible.</span>
          </h2>
          <p className="text-white/60 font-lato font-[400] text-[14px] lg:text-[18px] leading-relaxed">
            Thrive and collaborate with extraordinary people, build in-demand
            skills, and create meaningful work for clients across Fintech,
            Healthcare & Ecommerce.
          </p>
          <p className="text-white/40 font-lato font-[400] text-[13px] lg:text-[15px] leading-relaxed">
            We move fast, ship often, and celebrate every win together — big or
            small.
          </p>
        </div>
        <div className="h-[200px] lg:h-[380px] rounded-lg overflow-hidden">
          <img
            src={OfficeImg1}
            alt="Our culture"
            className="w-full h-full object-cover rounded-lg"
          />
        </div>
      </div>
    </div>
  );
}
const BottomGradient = () => {
  return (
    <>
      <span className="absolute inset-x-0 -bottom-px block h-px w-full bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 transition duration-500 group-hover/btn:opacity-100" />
      <span className="absolute inset-x-10 -bottom-px mx-auto block h-px w-1/2 bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-0 blur-sm transition duration-500 group-hover/btn:opacity-100" />
    </>
  );
};
function Environment() {
  return (
    <div className="bg-black h-screen px-5 pt-24 pb-10 lg:px-20 lg:py-0 flex items-center">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-20 w-full items-center">
        <div className="h-[200px] lg:h-[480px] rounded-lg overflow-hidden order-1">
          <img
            src={OfficeImg2}
            alt="Work environment"
            className="w-full h-full object-cover rounded-lg"
          />
        </div>
        <div className="flex flex-col gap-y-4 lg:gap-y-5 order-2">
          <p className="text-white/40 font-lato text-[12px] lg:text-[13px] font-[600] tracking-[0.2em] uppercase">
            Work environment
          </p>
          <h2 className="text-white font-lato font-[800] text-[1.6em] lg:text-[3.2em] leading-tight">
            Built for focused,{' '}
            <span className="text-[#24FFFF]">creative work.</span>
          </h2>
          <p className="text-white/60 font-lato font-[400] text-[14px] lg:text-[18px] leading-relaxed">
            Modern workspaces, top-of-the-line gear, and an async-first culture
            that gives you the freedom to do your best work — wherever you are.
          </p>
          <div className="flex flex-col gap-y-2 mt-2">
            {[
              'Remote & hybrid-friendly',
              'Zero micromanagement',
              'Quarterly team offsites',
            ].map(item => (
              <div key={item} className="flex items-center gap-x-3">
                <span className="text-[#24FFFF] text-[16px] leading-none">
                  ›
                </span>
                <p className="text-white font-lato font-[400] text-[13px] lg:text-[16px]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function People() {
  return (
    <div className="bg-black h-screen px-5 pt-24 pb-10 lg:px-20 lg:py-0 flex items-center">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-20 w-full items-center">
        <div className="flex flex-col gap-y-4 lg:gap-y-5">
          <p className="text-white/40 font-lato text-[12px] lg:text-[13px] font-[600] tracking-[0.2em] uppercase">
            Our people
          </p>
          <h2 className="text-white font-lato font-[800] text-[1.6em] lg:text-[3.2em] leading-tight">
            40+ builders across{' '}
            <span className="text-[#24FFFF]">3 continents.</span>
          </h2>
          <p className="text-white/60 font-lato font-[400] text-[14px] lg:text-[18px] leading-relaxed">
            Designers, developers, marketers, and strategists — a tight-knit
            team that ships fast, supports each other, and genuinely enjoys the
            work they do every day.
          </p>
          <div className="flex flex-col gap-y-2 mt-2">
            {[
              'India · Dubai · London',
              '50+ companies served',
              '100+ projects shipped',
              '13k+ five-star client reviews',
            ].map(item => (
              <div key={item} className="flex items-center gap-x-3">
                <span className="text-[#24FFFF] text-[16px] leading-none">
                  ›
                </span>
                <p className="text-white font-lato font-[400] text-[13px] lg:text-[16px]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="h-[200px] lg:h-[480px] rounded-lg overflow-hidden">
          <img
            src={OfficeImg3}
            alt="Our team"
            className="w-full h-full object-cover rounded-lg"
          />
        </div>
      </div>
    </div>
  );
}

function JoinUs() {
  const [openIndex, setOpenIndex] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const selectedRole = openIndex !== null ? openRoles[openIndex] : null;

  return (
    <>
      {modalOpen && (
        <ApplicationModal
          role={selectedRole}
          onClose={() => setModalOpen(false)}
        />
      )}

      <div className="bg-black h-screen px-5 pt-24 pb-10 lg:px-20 lg:py-0 flex flex-col justify-center gap-y-6 lg:gap-y-8 overflow-y-auto no-scrollbar">
        <p className="text-white/40 font-lato text-[12px] lg:text-[13px] font-[600] tracking-[0.2em] uppercase">
          Join the team
        </p>
        <h2 className="text-white font-lato font-[800] text-[2em] lg:text-[4.5em] leading-tight max-w-3xl">
          Ready to do the best{' '}
          <span className="text-[#24FFFF]">work of your life?</span>
        </h2>
        <p className="text-white/60 font-lato font-[400] text-[14px] lg:text-[20px] leading-relaxed max-w-2xl">
          We are looking for builders, thinkers, and doers who care deeply about
          craft. Join a team that ships meaningful products at scale — and has a
          great time doing it. No open roles listed? Reach out anyway.
        </p>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <button
            onClick={() => setModalOpen(true)}
            className="bg-primary w-fit text-white font-lato font-[500] text-[0.9em] lg:text-[1em] px-10 py-3 rounded-[30px] transition-opacity duration-200"
          >
            {selectedRole ? `Apply for ${selectedRole.title}` : 'Apply now'}
          </button>
          <a
            href="https://buildatscale.com/blog/"
            className="border border-white/25 w-fit text-white font-lato font-[400] text-[0.9em] lg:text-[1em] px-10 py-3 rounded-[30px] hover:border-white/50 transition-colors duration-200"
          >
            Contact Us
          </a>
        </div>

        <div className="w-full flex flex-col">
          {openRoles.map((role, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={role.index} className="border-t border-white/30">
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-center justify-between py-5 lg:py-6 text-left"
                >
                  <div className="flex items-center gap-x-6 lg:gap-x-10">
                    <span className="text-white/30 font-lato text-[11px] lg:text-[13px] font-[400] w-6">
                      {role.index}
                    </span>
                    <span className="text-white font-lato font-[600] text-[15px] lg:text-[22px]">
                      {role.title}
                    </span>
                  </div>
                  <span
                    className="text-white/40 text-[32px] leading-none select-none transition-transform duration-300 pr-1"
                    style={{
                      transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                    }}
                  >
                    +
                  </span>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: isOpen ? '400px' : '0px' }}
                >
                  <div className="pb-6 lg:pb-8 pl-12 lg:pl-16 flex flex-col gap-y-4">
                    <p className="text-white/40 font-lato font-[400] text-[13px] lg:text-[16px] leading-relaxed max-w-2xl">
                      {role.description}
                    </p>
                    <div className="flex flex-wrap gap-2 lg:gap-3">
                      {role.tags.map(tag => (
                        <span
                          key={tag}
                          className="border border-white/20 text-white font-lato font-[400] text-[11px] lg:text-[13px] px-4 py-2 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {/* <div className="border-t border-white/10" /> */}
        </div>

        <div className="border-t border-white/30 pt-6 lg:pt-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
            {[
              { stat: '50+', label: 'Companies served' },
              { stat: '100+', label: 'Projects shipped' },
              { stat: '40+', label: 'Team members' },
              { stat: '13k+', label: 'Five-star reviews' },
            ].map(item => (
              <div key={item.stat} className="flex flex-col gap-y-1">
                <p className="text-[#24FFFF] font-lato font-[800] text-[2em] lg:text-[2.8em] leading-none">
                  {item.stat}
                </p>
                <p className="text-white/50 font-lato font-[400] text-[12px] lg:text-[14px]">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default function Careers() {
  return (
    <>
      <Navbar />
      <Fullpage>
        <FullPageSections>
          <FullpageSection>
            <Culture />
          </FullpageSection>
          <FullpageSection>
            <Environment />
          </FullpageSection>
          <FullpageSection>
            <People />
          </FullpageSection>
          <FullpageSection>
            <JoinUs />
          </FullpageSection>
        </FullPageSections>
      </Fullpage>
    </>
  );
}
